import {
  AssetRecordType,
  Result,
  createShapeId,
  debounce,
  getHashForString
} from "@tldraw/editor";
const BOOKMARK_WIDTH = 300;
const BOOKMARK_HEIGHT = 320;
const BOOKMARK_JUST_URL_HEIGHT = 46;
const SHORT_BOOKMARK_HEIGHT = 101;
function getBookmarkHeight(editor, assetId) {
  const asset = assetId ? editor.getAsset(assetId) : null;
  if (asset) {
    if (!asset.props.image) {
      if (!asset.props.title) {
        return BOOKMARK_JUST_URL_HEIGHT;
      } else {
        return SHORT_BOOKMARK_HEIGHT;
      }
    }
  }
  return BOOKMARK_HEIGHT;
}
function setBookmarkHeight(editor, shape) {
  return {
    ...shape,
    props: { ...shape.props, h: getBookmarkHeight(editor, shape.props.assetId) }
  };
}
function getHumanReadableAddress(url) {
  try {
    const objUrl = new URL(url);
    return objUrl.hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}
function updateBookmarkAssetOnUrlChange(editor, shape) {
  const { url } = shape.props;
  const assetId = AssetRecordType.createId(getHashForString(url));
  if (editor.getAsset(assetId)) {
    if (shape.props.assetId !== assetId) {
      editor.updateShapes([
        {
          id: shape.id,
          type: shape.type,
          props: { assetId }
        }
      ]);
    }
  } else {
    editor.updateShapes([
      {
        id: shape.id,
        type: shape.type,
        props: { assetId: null }
      }
    ]);
    createBookmarkAssetOnUrlChange(editor, shape);
  }
}
function getResolvedBookmarkAssetId(editor, assetId, url) {
  if (assetId) return assetId;
  if (!url) return null;
  const derivedId = AssetRecordType.createId(getHashForString(url));
  return editor.getAsset(derivedId) ? derivedId : null;
}
function getBookmarkShapeHeight(editor, shape) {
  const resolvedAssetId = getResolvedBookmarkAssetId(editor, shape.props.assetId, shape.props.url);
  if (resolvedAssetId === shape.props.assetId) return shape.props.h;
  return getBookmarkHeight(editor, resolvedAssetId);
}
async function _createBookmarkAssetOnUrlChange(editor, shape) {
  if (editor.isDisposed) return;
  const { url } = shape.props;
  const asset = await editor.getAssetForExternalContent({ type: "url", url });
  if (!asset) {
    return;
  }
  editor.run(() => {
    editor.createAssets([asset]);
    editor.updateShapes([
      {
        id: shape.id,
        type: shape.type,
        props: { assetId: asset.id }
      }
    ]);
  });
}
const bookmarkDebouncers = /* @__PURE__ */ new WeakMap();
function createBookmarkAssetOnUrlChange(editor, shape) {
  let fn = bookmarkDebouncers.get(editor);
  if (!fn) {
    fn = debounce(_createBookmarkAssetOnUrlChange, 500);
    bookmarkDebouncers.set(editor, fn);
  }
  fn(editor, shape);
}
async function createBookmarkFromUrl(editor, {
  url,
  center = editor.getViewportPageBounds().center
}) {
  try {
    const expectedAssetId = AssetRecordType.createId(getHashForString(url));
    const existingAsset = editor.getAsset(expectedAssetId);
    const shapeId = createShapeId();
    const shapePartial = {
      id: shapeId,
      type: "bookmark",
      x: center.x - BOOKMARK_WIDTH / 2,
      y: center.y - BOOKMARK_HEIGHT / 2,
      rotation: 0,
      opacity: 1,
      props: {
        url,
        assetId: existingAsset?.id ?? null,
        w: BOOKMARK_WIDTH,
        h: getBookmarkHeight(editor, existingAsset?.id)
      }
    };
    editor.run(() => {
      editor.createShapes([shapePartial]);
    });
    const createdShape = editor.getShape(shapeId);
    if (!createdShape) {
      return Result.err("Failed to create bookmark shape");
    }
    if (existingAsset) {
      return Result.ok(createdShape);
    }
    void hydrateBookmarkShape(editor, shapeId, url);
    return Result.ok(createdShape);
  } catch (error) {
    return Result.err(error instanceof Error ? error.message : "Failed to create bookmark");
  }
}
async function hydrateBookmarkShape(editor, shapeId, url) {
  let asset;
  try {
    asset = await editor.getAssetForExternalContent({ type: "url", url });
  } catch (error) {
    console.error(error);
    return;
  }
  if (editor.isDisposed) return;
  if (!asset) return;
  const shape = editor.getShape(shapeId);
  if (!shape) return;
  if (shape.props.url !== url) return;
  if (shape.props.assetId) return;
  editor.run(
    () => {
      if (!editor.getAsset(asset.id)) {
        editor.createAssets([asset]);
      }
      editor.updateShapes([
        {
          id: shapeId,
          type: "bookmark",
          props: { assetId: asset.id }
        }
      ]);
    },
    { history: "ignore" }
  );
}
export {
  BOOKMARK_HEIGHT,
  BOOKMARK_JUST_URL_HEIGHT,
  BOOKMARK_WIDTH,
  createBookmarkFromUrl,
  getBookmarkHeight,
  getBookmarkShapeHeight,
  getHumanReadableAddress,
  getResolvedBookmarkAssetId,
  setBookmarkHeight,
  updateBookmarkAssetOnUrlChange
};
//# sourceMappingURL=bookmarks.mjs.map
